import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Check, Plus, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";

/**
 * Header control to switch the active athlete and add a new one (up to the plan's
 * tier cap, enforced server-side). Renders nothing until the user has an athlete;
 * the first athlete is created during onboarding.
 */
export function AthleteSwitcher() {
  const { athletes, athlete, setActiveAthleteId } = useActiveAthlete();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [sports, setSports] = useState("");

  const createAthlete = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/athletes", {
        name: name.trim(),
        sports: sports.trim(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Could not add athlete.");
      }
      return res.json();
    },
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      if (created?.id) setActiveAthleteId(created.id);
      toast({ title: "Athlete added", description: `${created?.name ?? "New athlete"} is ready.` });
      setAddOpen(false);
      setName("");
      setSports("");
    },
    onError: (e: Error) => {
      toast({ title: "Could not add athlete", description: e.message, variant: "destructive" });
    },
  });

  if (!athlete) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex max-w-[160px] items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-white hover:bg-[#1B263B]"
            aria-label="Switch athlete"
          >
            <span className="truncate">{athlete.name ?? "Athlete"}</span>
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#E0A458]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-[#E0A458]/40 bg-[#111827] text-slate-100">
          {athletes.map((a) => (
            <DropdownMenuItem
              key={a.id}
              onClick={() => setActiveAthleteId(a.id)}
              className="hover:bg-[#1B263B]"
            >
              <Check
                className={`mr-2 h-4 w-4 ${a.id === athlete.id ? "text-[#E0A458]" : "opacity-0"}`}
              />
              <span className="truncate">{a.name ?? "Athlete"}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-[#415A77]/60" />
          <DropdownMenuItem onClick={() => setAddOpen(true)} className="hover:bg-[#1B263B]">
            <Plus className="mr-2 h-4 w-4 text-[#E0A458]" />
            Add athlete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="border-[#1B263B] bg-[#0D1B2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#E0A458]">Add an athlete</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && sports.trim()) createAthlete.mutate();
            }}
            className="space-y-4"
          >
            <div>
              <Label className="mb-1 block text-sm text-slate-300">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jordan Rivers"
                className="border-[#1B263B] bg-[#0f2740] text-white"
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm text-slate-300">Sport</Label>
              <Input
                value={sports}
                onChange={(e) => setSports(e.target.value)}
                placeholder="e.g. Softball"
                className="border-[#1B263B] bg-[#0f2740] text-white"
              />
            </div>
            <p className="text-xs text-slate-400">
              You can add more details on the athlete's profile after creating them.
            </p>
            <DialogFooter>
              <Button
                type="submit"
                disabled={!name.trim() || !sports.trim() || createAthlete.isPending}
                className="bg-[#E0A458] font-semibold text-black hover:bg-[#f0b467]"
              >
                {createAthlete.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add athlete"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
